import { Component, OnInit, ViewChild, HostListener, ElementRef } from '@angular/core';
import { ClipboardService, FormsClipboardServiceFactory } from 'app/services/clipboard.service';
import { FormTemplate, FormTemplateType } from 'app/models/template';
import { FormGroup, FormBuilder, Validators, AsyncValidatorFn, AbstractControl, ValidationErrors, FormControl } from '@angular/forms';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { MDBModalService, ModalDirective, ToastService } from 'ng-uikit-pro-standard';
import { FormTemplateService } from 'app/services/form-template.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ValidationHandler } from 'app/helpers/validation.handler';
import { basename } from 'path';
import { catchError, filter, finalize, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { Observable, forkJoin, of, Subject, timer } from 'rxjs';
import { Clipboard } from 'app/models/clipboard';
import { AbstractPickedFile, StorageTypeEnum, LocalPickedFile, CloudPickedFile } from 'app/models/picked-file';
import { FilesFoldersPickerComponent } from 'app/shared/components/files-folders-picker/files-folders-picker.component';
import { FormFillerComponent } from '../form-filler/form-filler.component';
import { EcoDocsTitleService } from 'app/services/ecodocs-title.service';
import { UserService } from 'app/services/user.service';
import { SpreadsheetToFormComponent } from '../spreadsheet-to-form/spreadsheet-to-form.component';
import { kebabCase, trimEnd } from 'lodash';
import { FeatureTrialLockModalComponent } from '../feature-trial-lock-modal/feature-trial-lock-modal.component';

@Component({
  selector: 'app-forms-explorer',
  templateUrl: './forms-explorer.component.html',
  styleUrls: ['./forms-explorer.component.scss'],
  providers: [
    {
      provide: ClipboardService,
      useFactory: FormsClipboardServiceFactory
    }
  ]
})
export class FormsExplorerComponent implements OnInit {
  currentPath: string[] = [];

  filesLoadingIndicator: boolean = true;
  currentStorageName: string = 'Forms';

  forms: FormTemplate[] = [];
  selectedRows: FormTemplate[] = [];
  versionHistoryForm: FormTemplate = null;

  pdfFileToUpload: File = null;
  pdfStorageId: number = null;
  pdfPath: string = null;
  isCreatingForm: boolean = false;
  isCreatingFolder: boolean = false;
  isRenamingFolder: boolean = false;
  isSearchOpen: boolean = false;
  hasClipboard: boolean = false;
  isLoadingVersionHistory: boolean = false;

  indexBasenameSubmitSubject: Subject<string> = new Subject();
  indexBasenameFormGroup: FormGroup;
  
  newFormSubmitSubject: Subject<string> = new Subject();
  createNewFormFormGroup: FormGroup;
  createNewFolderFormGroup: FormGroup;
  renameFormGroup: FormGroup;

  searchText: string = '';
  searchInContent: boolean = true;

  canCreate: boolean = false;
  canUpdate: boolean = false;
  canSubmit: boolean = false;
  canViewData: boolean = false;

  @ViewChild(DatatableComponent, { static: true }) table: DatatableComponent;
  @ViewChild('createFolderModal', { static: true }) createFolderModal: ModalDirective;
  @ViewChild('trialLockModal', { static: true }) trialLockModal: ModalDirective;
  @ViewChild('deleteConfirmationModal', { static: true }) deleteConfirmationModal: ModalDirective;
  @ViewChild('renameFileModal', { static: true }) renameFileModal: ModalDirective;
  @ViewChild('versionHistoryModal', { static: true }) versionHistoryModal: ModalDirective;
  @ViewChild('createFormModal', { static: true }) createFormModal: ModalDirective;
  @ViewChild('indexBasenameModal', { static: true }) indexBasenameModal: ModalDirective;
  @ViewChild('pdfPicker') pdfPicker: FilesFoldersPickerComponent;
  @ViewChild('formFiller') formFiller: FormFillerComponent;
  @ViewChild('spreadsheetToForm') spreadsheetToForm: SpreadsheetToFormComponent;

  currentFormHistory: FormTemplate[] = [];

  muliSelectionOptions = [
    /*{
      label: 'Star',
      action: () => {
        console.log('clicked')
      }
    },*/
    {
      label: 'Delete',
      action: () => {
        this.deleteSelected();
      },
      isEnabled: this.canDelete.bind(this)
    }
  ]

  folderOptions = [
    {
      label: 'Open',
      action: () => this.onFolderRightClickOpen(),
      isEnabled: () => true
    },
    {
      label: 'Cut',
      action: () => this.onMoveOrCopyFile('cut'),
      isEnabled: this.canMove.bind(this)
    },
    {
      label: 'Copy',
      action: () => this.onMoveOrCopyFile('copy'),
      isEnabled: this.canCopy.bind(this)
    },
    {
      label: 'Delete',
      action: () => this.deleteSelected(),
      isEnabled: this.canDelete.bind(this)
    },
    {
      label: 'Rename',
      action: () => this.onFileRenaming(),
      isEnabled: this.canRename.bind(this)
    }
  ]

  fileOptions = [
    {
      label: 'Fill Form',
      action: () => {
        this.openForm(this.selectedRows[0]);
      },
      isEnabled: () => this.canSubmit
    },
    {
      label: 'Edit Form',
      action: () => { 
        this.loadDraftAndEdit();
      },
      isEnabled: ()=> this.canUpdate
    },  
    {
      label: 'View Data',
      action: () => { 
        this.showData();
      },
      isEnabled: this.canShowData
    },
    {
      label: 'Cut',
      action: () => this.onMoveOrCopyFile('cut'),
      isEnabled: this.canMove.bind(this)
    },
    {
      label: 'Copy',
      action: () => this.onMoveOrCopyFile('copy'),
      isEnabled: this.canCopy.bind(this)
    },
    {
      label: 'Delete',
      action: () => this.deleteSelected(),
      isEnabled: this.canDelete.bind(this)
    },
    {
      label: 'Rename',
      action: () => this.onFileRenaming(),
      isEnabled: this.canRename.bind(this)
    },
    {
      label: 'Version History',
      action: () => {
        this.loadVersionHistory();
      },
      isEnabled: () => this.canUpdate
    },
    {
      label: 'Import Spreadsheet',
      action: () => {
        this.importSpreadsheetInForm();
      },
      isEnabled: () => this.canImportIntoForm.bind(this)
    },
    /*{
      label: 'Convert to PDF',
      action: () => {
        console.log('clicked')
      }
    }*/
    // {
    //   label: 'Workflows',
    //   subMenu: [
    //     {
    //       label: 'workflow 1',
    //       action: () => {
    //         console.log('clicked')
    //       }
    //     },
    //     {
    //       label: 'workflow 2',
    //       action: () => {
    //         console.log('clicked')
    //       }
    //     }
    //   ]
    // },

  ];

  newFileOptions = [
    {
      label: 'New Form',
      action: () => { 
        this.pdfStorageId = null;
        this.pdfPath = null;      
        this.pdfFileToUpload = null;
        this.createFormModal.show();
      },
      isEnabled: () => this.canCreate
    },
    {
      label: 'New Folder',
      action: () => {
        this.createFolderModal.show()
      },
      isEnabled: () => this.canCreate
    },
    {
      label: 'Form from Spreadsheet',
      action: () => {
        this.spreadsheetToForm.startWizard()
      },
      isEnabled: () => this.canCreate
    }
  ]

  isContextOpen: boolean = false;
  contextTop: number = 0;
  contextLeft: number = 0;
  contextOptions: any[] = [];
  showFileOverView: boolean = false;
  rightSideContext: any[] = [];

  constructor(public formTemplateService: FormTemplateService, public router: Router, private formBuilder: FormBuilder,
    private validationHandler: ValidationHandler, private toastService: ToastService,  private userService: UserService,
    public formsClipboardService: ClipboardService, private activatedRoute: ActivatedRoute,private ecoDocsTitleService: EcoDocsTitleService,
    private modalService: MDBModalService) {

      this.createNewFolderFormGroup = this.formBuilder.group({
        folder_name: ['', [Validators.required, this.formTemplateService.illegalFormCharsValidator]]
      });
  
      this.createNewFormFormGroup = this.formBuilder.group({
        form_name: ['', [Validators.required, this.formTemplateService.illegalFormCharsValidator]],
        index_basename: ['', {validators: [Validators.required, Validators.maxLength(160), this.formTemplateService.lowercaseValidator, this.formTemplateService.illegalIndexBasenameStartValidator, this.formTemplateService.illegalIndexBasenameCharsValidator], asyncValidators: this.formTemplateService.dataIndexNameValidator()}]
      });

      this.indexBasenameFormGroup = this.formBuilder.group({
        index_basename: ['', {validators: [Validators.required, this.formTemplateService.lowercaseValidator, this.formTemplateService.illegalIndexBasenameStartValidator, this.formTemplateService.illegalIndexBasenameCharsValidator], asyncValidators: this.formTemplateService.dataIndexNameValidator()}]
      });

      this.renameFormGroup = this.formBuilder.group({
        dest_path: ['', Validators.required]
      });
  
      this.newFormSubmitSubject.pipe(
        tap(()=> { 
            this.validationHandler.onSubmitCheck(this.createNewFormFormGroup);
          }),
        switchMap(() =>
          this.createNewFormFormGroup.statusChanges.pipe(
            startWith(this.createNewFormFormGroup.status),
            filter(status => status !== 'PENDING'),
            take(1)
          )
        ),
      ).subscribe((status) => {
        if (status == 'VALID') {
          this.onCreateNewForm();
        } else {
          // invalid, do nothing, errors will be displayed
        }
      });

      this.indexBasenameSubmitSubject.pipe(
        tap(()=> { 
            this.validationHandler.onSubmitCheck(this.indexBasenameFormGroup);
          }),
        switchMap(() =>
          this.indexBasenameFormGroup.statusChanges.pipe(
            startWith(this.indexBasenameFormGroup.status),
            filter(status => status !== 'PENDING'),
            take(1)
          )
        ),
      ).subscribe((status) => {
        if (status == 'VALID') {
          this.indexBasenameModal.hide();
          this.onPasteFile();
        } else {
          // invalid, do nothing, errors will be displayed
        }
      });
  }

  suggestIndexBaseName() {
    const currentPath = this.currentPath.length > 0 ? this.currentPath[this.currentPath.length - 1] : '';
    this.createNewFormFormGroup.get('index_basename').setValue(kebabCase(currentPath + '/' + this.createNewFormFormGroup.get('form_name').value));
  }

  loadVersionHistory() {
    this.currentFormHistory = [];
    this.isLoadingVersionHistory = true;
    this.versionHistoryModal.show();
    this.formTemplateService.versions(this.selectedRows[0].id).pipe(finalize(() => {
      this.isLoadingVersionHistory = false;
    })).subscribe((versions) => {
      this.currentFormHistory = versions;
    });
  }

  loadDraftAndEdit() {
    this.filesLoadingIndicator = true;
    this.formTemplateService.getOrCreateDraft(this.selectedRows[0]).pipe(finalize(() => {
      this.filesLoadingIndicator = false;
    })).subscribe((f) => {
      // open the builder with that draft
      this.onEditForm(f);
    });
  }

  showData() {
    this.router.navigate([`form-data/${this.selectedRows[0].id}`]);
  }

  showTrash() {
    this.router.navigate([`deleted-forms`]);
  }

  ngOnInit(): void {
    this.ecoDocsTitleService.setTitle('Forms Explorer', false);
    this.searchText = '';
    this.isLoadingVersionHistory = false;
    this.currentFormHistory = [];
    this.formsClipboardService.onClipboardChanged$.subscribe((formsClipboard) => {
      if (formsClipboard !== null)
        this.hasClipboard = true;
      else
        this.hasClipboard = false;
    });

    this.activatedRoute.params.subscribe(params => {
      this.currentPath = [];
      let path: string = params['path']? params['path']: '/';
      let pathParts: string[] = path.split('/');
      let concatPath = '';
      pathParts.forEach((dir) => {
        // skip empty dirs
        if (dir.length > 0) {
          concatPath += '/' + dir;
          this.currentPath.push(concatPath);
        }
      });
      this.getAllForms();
    });

    this.getAllForms();
  }

  getAllForms() {
    this.filesLoadingIndicator = true;

    let lastDir: string = (this.currentPath[this.currentPath.length - 1])? this.currentPath[this.currentPath.length - 1]: '';

    this.canCreate = this.userService.canForPath('super.admin.forms.create', lastDir);
    this.canUpdate = this.userService.canForPath('super.admin.forms.create.update', lastDir);
    this.canSubmit = this.userService.canForPath('super.admin.forms.create.update.view', lastDir);
    this.canViewData = this.userService.canForPath('super.admin.data.delete.update.view', lastDir);

    // update the current path in the url
    this.router.navigateByUrl(this.router.createUrlTree(['forms-explorer', lastDir], {}));

    this.formTemplateService.list(this.currentPath[this.currentPath.length - 1]).subscribe((response: FormTemplate[]) => {
      this.forms = response;
      this.filesLoadingIndicator = false;
      this.selectedRows = [];
    }, err => {
      this.toastService.error('Error listing forms!');
    });
  }
  innerWidth() {
    return window.innerWidth;
  }

  updateRightSideContextMenu() {
    //console.log(event)
    if (this.selectedRows.length > 1) {
      this.rightSideContext = this.muliSelectionOptions;
    }
    else if (this.selectedRows.length == 1) {
      if (this.selectedRows[0].form_type_id == FormTemplateType.FOLDER) {
        this.rightSideContext = this.folderOptions;
      }
      else {
        this.rightSideContext = this.fileOptions;
      }
    } else {
      this.rightSideContext = [];
    }
  }
  onTableRowSelect(event) {
    this.updateRightSideContextMenu();
  }

  toggleContext(event) {
    if (event.type == 'body') {

      this.isContextOpen = false;

      if (!this.selectedRows.find(r => r == event.content))
        this.selectedRows.push(event.content)

      if (this.selectedRows.length > 1) {
        this.contextOptions = this.muliSelectionOptions
      }
      else {
        event.content.form_type_id == FormTemplateType.FOLDER ? this.contextOptions = this.folderOptions : this.contextOptions = this.fileOptions;
      }
      /** @todo investigate why we need to subtract the header's height to get the correct Y */
      this.contextTop = event.event.pageY - 50; // 50 for the header height
      this.contextLeft = event.event.pageX;
      this.isContextOpen = true;
      event.event.preventDefault();
      event.event.stopPropagation();
    }
    // console.log(event)
  }

  toggleFileOverview(event, state, row) {
    if (state == true) {
      this.selectedRows = [row];
    }
    event.stopPropagation();
    this.showFileOverView = state;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event) {
    this.isContextOpen = false;
  }

  toggleSearch() {
    this.isSearchOpen = !this.isSearchOpen;
  }

  selectAll() {
    this.selectedRows = this.forms.slice();
  }

  deselectAll() {
    this.selectedRows = [];
  }

  invertSelection() {
    let tempSelectedRows = [];
    this.forms.forEach(form => {
      if (!this.selectedRows.find(f => f == form)) {
        tempSelectedRows.push(form);
      }
    })
    this.selectedRows = tempSelectedRows;
  }

  canDeselect() {
    return this.selectedRows.length > 0;
  }

  canSelectAll() {
    return this.selectedRows.length != this.forms.length;
  }

  canDelete() {
    return this.canUpdate && this.selectedRows.length > 0;
  }

  canCopy() {
    return this.canUpdate && this.selectedRows.length == 1 && (this.selectedRows[0] as FormTemplate).form_type_id != FormTemplateType.FOLDER;
  }

  canImportIntoForm() {
    return this.canUpdate && this.selectedRows.length == 1 && (this.selectedRows[0] as FormTemplate).form_type_id != FormTemplateType.FOLDER && !(this.selectedRows[0] as FormTemplate).is_draft;
  }

  canMove() {
    return this.canCopy() && this.canDelete();
  }
  canPaste() {
    return this.canCreate && this.hasClipboard;
  }
  
  canRename() {
    return this.canCreate && this.selectedRows.length == 1;
  }

  canEdit() {
    return this.canUpdate && this.selectedRows.length == 1 && (this.selectedRows[0] as FormTemplate).form_type_id != FormTemplateType.FOLDER;
  }

  canShowData() {
    return this.canViewData && this.selectedRows.length == 1 && (this.selectedRows[0] as FormTemplate).form_type_id != FormTemplateType.FOLDER && !this.selectedRows[0].is_draft;
  }

  canShowVersions() {
    return this.canUpdate && this.selectedRows.length == 1 && (this.selectedRows[0] as FormTemplate).form_type_id != FormTemplateType.FOLDER;
  }

  getSelectedFileLocation() {
    const lastseparator = this.currentPath.length > 0 ? ' > ' : '';
    return this.currentStorageName + ' > ' + this.currentPath.map(p => basename(p)).join(' > ') + lastseparator + basename(this.selectedRows[0].path);
  }

  // open specific folder from current path (my files > folder1 > folder2)
  openParentFolder(folder: string) {
    const index = this.currentPath.findIndex(p => p == folder);

    if (index > -1) {
      this.currentPath.splice(index + 1);
    }

    this.getAllForms();
  }

  // open folder when user pressed right click on folder and then choose open
  onFolderRightClickOpen() {
    const dir = (<FormTemplate>this.selectedRows.pop()).path;
    this.currentPath.push(dir);
    this.getAllForms();
  }

  openRootFolder() {
    this.currentPath.splice(0);
    this.getAllForms();
  }

  clearSelection() {
    if (window.getSelection) {
      var sel = window.getSelection();
      sel.removeAllRanges();
    }
  }

  selectDeselectAllForms(isChecked: boolean) {
    this.selectedRows = (isChecked)? [...this.forms]: [];
  }

  importSpreadsheetInForm() {
    this.spreadsheetToForm.startWizard(<FormTemplate>this.selectedRows[0]);
  }

  onactivate(event: any) {
    if (event.type == "click") {
      const row = <FormTemplate>event.row;
      this.clearSelection();

      // open folder on double click
      if (row.form_type_id === FormTemplateType.FOLDER) {
        if (!this.filesLoadingIndicator) {
          this.selectedRows = [];
          this.currentPath.push(row.path);
          this.getAllForms();
        }
      } else {
        this.openForm(row);
      }
    }
  }

  openForm(formTemplate: FormTemplate) {
    if (formTemplate.is_draft) {
      this.toastService.error('Draft forms cannot be submitted. Publish this form to submit data.');
      return;
    }
    if (!this.canSubmit) {
      this.toastService.error('Not enough permissions to open this form for data submission.')
      return;
    }

    if (!this.userService.currentUserValue.subscription_status.is_subscribed &&
        !this.userService.currentUserValue.subscription_status.trial_active) {
          this.trialLockModal.show();
          return;
    }

    // Open this form for filling
    this.filesLoadingIndicator = true;
    this.formTemplateService.getById(formTemplate.id).pipe(finalize(() => {
      this.filesLoadingIndicator = false;
    })).subscribe((f) => {
      this.formFiller.show(f);
    });
  }

  onFormFilled(event: any) {
    this.filesLoadingIndicator = true;
    this.formTemplateService.putSubmission(event.submission).pipe(finalize(() => {
      this.filesLoadingIndicator = false;
    })).subscribe((f) => {
      this.toastService.success('Saved!');
    }, error => {
      this.toastService.error('Error saving data!');
    });
  }

  onAddNewFolder() {
    this.validationHandler.onSubmitCheck(this.createNewFolderFormGroup);
    if (!this.createNewFolderFormGroup.valid) {
      return;
    }
    const path = (this.currentPath.length > 0 ? this.currentPath[this.currentPath.length - 1] : "/");

    this.isCreatingFolder = true;
    this.formTemplateService.createFolder(this.createNewFolderFormGroup.controls["folder_name"].value, path).pipe(finalize(() => {
      this.isCreatingFolder = false;
    })).subscribe(() => {
      this.getAllForms();
      this.createFolderModal.hide();
      this.createNewFolderFormGroup.reset();
    }, err => {
      this.validationHandler.serverValidationHandler(err, this.createNewFolderFormGroup);
    });
  }

  onPickedPDF(e: AbstractPickedFile[]) {
    if (e[0].storageType == StorageTypeEnum.LOCAL) {
      this.pdfFileToUpload = (e[0] as LocalPickedFile).file;
    } else {
      this.pdfStorageId = (e[0] as CloudPickedFile).storageId;
      this.pdfPath = (e[0] as CloudPickedFile).path;
    }
    this.createFormModal.show();
  }

  onCreateNewForm() {
    // this.validationHandler.onSubmitCheck(this.createNewFormFormGroup);
    // if (!this.createNewFormFormGroup.valid) {
    //   return;
    // }

    this.isCreatingForm = true;

    const path = (this.currentPath.length > 0 ? trimEnd(this.currentPath[this.currentPath.length - 1], '/') : "");
    this.formTemplateService.getOrCreateDraft({
      path: path + '/' + this.createNewFormFormGroup.controls["form_name"].value,
      form_type_id: (this.pdfFileToUpload || this.pdfPath)? FormTemplateType.PDF: FormTemplateType.WEBFORM,
      index_basename: this.createNewFormFormGroup.controls['index_basename'].value
    } as FormTemplate, this.pdfFileToUpload, this.pdfStorageId, this.pdfPath).pipe(finalize(() => {
      this.isCreatingForm = false;
    })).subscribe((f) => {
      this.createFormModal.hide();
      this.createNewFormFormGroup.reset();
      // open the builder with that draft
      this.onEditForm(f);
      //this.refresh();
    }, err => {
      this.validationHandler.serverValidationHandler(err, this.createNewFormFormGroup);
    });
  }

  deleteSelected() {
    if (this.selectedRows.length > 0) {
      this.filesLoadingIndicator = true;
      const observables: Observable<any>[] = []
      this.selectedRows.forEach(form => {
        if (form.form_type_id === FormTemplateType.FOLDER) {
          observables.push(this.formTemplateService.deleteFolder(form.path));
        } else {
          observables.push(this.formTemplateService.deleteForm(form.path));
        }
      });

      forkJoin(observables).subscribe(() => {
        // clear selection
        this.selectedRows = [];
        this.getAllForms();
      }, err => {
        this.filesLoadingIndicator = false;
        this.toastService.error('Error Deleting Form(s)!');
        // clear selection
        this.selectedRows = [];
      });
    }
  }
  
  showDeleteConfirmation() {
    if (this.selectedRows.length > 0) {
      this.deleteConfirmationModal.show();
    }
  }
  onDeleteForm() {
    this.deleteConfirmationModal.hide();
    this.deleteSelected();
  }

  onMoveOrCopyFile(op_name: string) {

    this.formsClipboardService.setClipboard(op_name, null, this.selectedRows);

    if (op_name === 'cut') {
      this.toastService.success(`${this.selectedRows.length} Item(s) Cut!`);
    } else if (op_name === 'copy') {
      this.toastService.success(`${this.selectedRows.length} Item(s) Copied!`);
    }

    //this.hasClipboard = true;
    this.selectedRows = [];
  }

  onPasteFile() {
    let clipboard: Clipboard = this.formsClipboardService.getClipboard();

    if (clipboard && clipboard.clipboardItems.length > 0) {
      this.filesLoadingIndicator = true;

      const currentPath = this.currentPath.length > 0 ? this.currentPath[this.currentPath.length - 1] : '';

      let pasteObservables: Observable<any>[] = [];
      const isCut: boolean = clipboard.operation_name === 'cut';

      clipboard.clipboardItems.forEach((clipboardItem) => {
        const filename = basename(clipboardItem.path);

        if (isCut) {
          if (clipboardItem.type != FormTemplateType.FOLDER) {
            pasteObservables.push(this.formTemplateService.moveForm(clipboardItem.path, trimEnd(currentPath, '/') + '/' + filename).pipe(tap(null, (err) => {
              if (err && err.status == 422 && err.message) {
                this.toastService.error(err.message);
              }
            })));
          } else {
            pasteObservables.push(this.formTemplateService.moveFolder(clipboardItem.path, trimEnd(currentPath, '/') + '/' + filename));
          }
        } else {
          if (clipboardItem.type != FormTemplateType.FOLDER) {
            pasteObservables.push(this.formTemplateService.copyForm(clipboardItem.path, trimEnd(currentPath, '/') + '/' + filename, this.indexBasenameFormGroup.get('index_basename').value));
            this.indexBasenameFormGroup.reset();
          } else {
            pasteObservables.push(this.formTemplateService.copyFolder(clipboardItem.path, trimEnd(currentPath, '/') + '/' + filename));
          }
        }
      });

      forkJoin(pasteObservables).subscribe(() => {
        this.getAllForms();
      }, (err) => {
        this.filesLoadingIndicator = false;
        this.toastService.error('Error Pasting Form(s)!');
      });

      if (clipboard.operation_name === 'cut') {
        this.formsClipboardService.clearClipboard();
      }
    }
    this.refresh();
  }

  onEditForm(formTemplate: FormTemplate) {
    this.router.navigate([`/form-builder/${formTemplate.id}`]);
  }

  onFileRenaming() {
    this.renameFileModal.show();
    this.renameFormGroup.controls['dest_path'].setValue(basename(this.selectedRows[0].path));
  }

  onRenameFile() {
    this.validationHandler.onSubmitCheck(this.renameFormGroup);
    if (!this.renameFormGroup.valid) {
      return;
    }

    const currentPath = this.currentPath.length > 0 ? this.currentPath[this.currentPath.length - 1] : '';
    const filePath = this.selectedRows[0].path;

    this.isRenamingFolder = true;
    if (this.selectedRows[0].form_type_id != FormTemplateType.FOLDER) {
      this.formTemplateService.moveForm(filePath, trimEnd(currentPath, '/') + '/' + this.renameFormGroup.controls['dest_path'].value).pipe(finalize(() => {
        this.isRenamingFolder = false;
      })).subscribe(() => {
        this.getAllForms();
        this.renameFormGroup.reset();
        this.selectedRows = [];
        this.renameFileModal.hide();
      }, err => {
        this.validationHandler.serverValidationHandler(err, this.renameFormGroup);
      });
    }
    else {
      this.formTemplateService.moveFolder(filePath, trimEnd(currentPath, '/') + '/' + this.renameFormGroup.controls['dest_path'].value).pipe(finalize(() => {
        this.isRenamingFolder = false;
      })).subscribe(() => {
        this.getAllForms();
        this.renameFormGroup.reset();
        this.selectedRows = [];
        this.renameFileModal.hide();
      }, err => {
        this.validationHandler.serverValidationHandler(err, this.renameFormGroup);
      });
    }
  }

  refresh()
  {
    this.getAllForms()
    this.selectedRows = [];
  }

  columnComparator(value1: any, value2: any, form1: FormTemplate, form2: FormTemplate) {
    let isForm1Folder: boolean = form1.form_type_id === FormTemplateType.FOLDER;
    let isForm2Folder: boolean = form2.form_type_id === FormTemplateType.FOLDER;

    if (isForm1Folder && !isForm2Folder) {
      return -1;
    } else if (!isForm1Folder && isForm2Folder) {
      return 1;
    }

    if (value1.toLowerCase || value2.toLowerCase)
      return value1.toLowerCase().localeCompare(value2.toLowerCase());
    else
      return (value1 > value2) ? 1 : -1;
  }

  onRestoreVersion(formTemplate: FormTemplate) {
    this.isLoadingVersionHistory = true;
    this.formTemplateService.restoreVersion(formTemplate.id, formTemplate.version).pipe(finalize(() => {
      this.isLoadingVersionHistory = false;
      this.loadVersionHistory();
      this.refresh();
    })).subscribe();
  }

  beginSearch() {
    this.router.navigate(['/search'], {queryParams: {
      options: JSON.stringify({
          query: this.searchText,
          is_files: false,
          is_user_storage: false,
          is_global_storage: false,
          in_file_contents: false,
          is_forms: true,
          in_form_contents: this.searchInContent,
          forms_folders: (this.currentPath.length)? [{path: this.currentPath[this.currentPath.length - 1]}]: [],
          is_data: false
      })
    }});
  }
}
